/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles, styled } from '@mui/styles';
import {
  CircularProgress,
  Grid2,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
} from '@mui/material';
import { useNavigate } from 'react-router';
import { AddIcon, EditIcon, PublishIcon, UploadIcon } from '../../shared/components/AppIcons';
import { useAuthentication } from '../../auth/services/auth.provider';
import SortableTableHead, { SortableTableHeadCell } from '../../shared/components/SortableTableHead';
import theme from '../../../core/theme';
import DashboardService from '../services/dashboard.services';
import moment from 'moment';
import { Activity, ActivityQueryParams } from '../../../data/types/user.interface';
import { lowerCase } from 'lodash';
import ContentDetails from '../../content-collection/components/contentDetails';
import PreFinalFileContentDetails from '../../content-creation/components/partA/preFinalFileContentDetails';
import FinalFileContentDetails from '../../content-creation/components/partB/finalFileContentDetails';

const useStyles = makeStyles({
  welcomeText: {
    height: '82px',
    fontSize: '36px',
  },
  container: {
    width: '293px',
    height: '196px',
    textAlign: 'center',
    borderRadius: '4px',
    cursor: 'pointer',
    border: '1px solid rgb(236, 229, 229)',
    '&:hover': {
      border: '1px solid #F67570',
      boxShadow: '0 4px 4px 0 rgba(246, 117, 112, 0.09)',
    },
  },
  info: {
    fontSize: '16px',
    color: '#939393',
  },
  icon: {
    fontSize: '70px !important',
  },
  table: {
    '& .MuiTableHead-root': {
      backgroundColor: '#30343F',
      color: '#fff !important',
      fontSize: '17px',
      '& .MuiTableCell-root': {
        paddingLeft: '0',
      },
      '& .MuiTableSortLabel-root.Mui-active': {
        color: '#fff !important',
        '&:hover': {
          color: '#fff !important',
        },
      },
      '& .MuiTableSortLabel-icon': {
        color: '#fff !important',
      },
    },
  },
  hover: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
});

const StyledTableCell: any = styled(TableCell)(() => ({
  paddingTop: '12px',
  paddingBottom: '12px',
  borderBottom: `1px solid ${theme.palette.chipBg.main}`,
}));

const Dashboard: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { currentUser } = useAuthentication();
  const [columnHeader, setColumnHeader] = useState<SortableTableHeadCell<any>[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [listFilterParams, setListFilterParams] = useState<ActivityQueryParams>({ perPage: 10, page: 1 });
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [openDetailModal, setOpenDetailModal] = useState<boolean>(false);
  const [documentId, setDocumentId] = useState<number | null>(null);
  const [fileType, setFileType] = useState<string>('');

  const isAdminOrMarketer = currentUser?.role.name === 'admin' || currentUser?.role.name === 'marketer';

  const handleClick = (id: string) => {
    switch (id) {
      case 'contentCollection':
        navigate('/dashboard/content-collection');
        break;
      case 'contentCreation':
        navigate('/dashboard/content-creation');
        break;
      case 'contentMarketing':
        isAdminOrMarketer ? navigate('/dashboard/content-marketing') : navigate('/unauthorized');
        break;
      case 'marketingAnalytics':
        isAdminOrMarketer ? navigate('/dashboard/content-marketing/analytics') : navigate('/unauthorized');
        break;
    }
  };

  const handleUserActivity = useCallback(async () => {
    setIsLoading(true);
    try {
      const contents = await DashboardService.getUserActivities(listFilterParams);
      setActivities(contents.activities);
      setTotalCount(contents.totalCount);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [listFilterParams]);

  const handleChangePage = (e: any, newPage: number) => {
    setListFilterParams((prev) => ({
      ...prev,
      page: newPage + 1,
    }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setListFilterParams({
      page: 1,
      perPage: parseInt(event.target.value, 10),
    });
  };

  useEffect(() => {
    setColumnHeader([
      { id: 'activity', label: 'Activity', numeric: false, sortable: false },
      { id: 'type', label: 'File Type', numeric: false, sortable: false },
      { id: 'dateAdded', label: 'Date Added', numeric: false, sortable: false },
      { id: 'useName', label: 'User Name', numeric: false, sortable: false },
    ]);
  }, []);

  useEffect(() => {
    handleUserActivity();
  }, [handleUserActivity]);

  const handleDetailModal = (activity: Activity) => {
    setOpenDetailModal(true);
    setDocumentId(activity.id);
    setFileType(activity.fileType);
  };

  return (
    <Grid2 container ml={2} mt={2} direction="column">
      <Grid2 className={classes.welcomeText} mb={2}>
        Welcome to the DWD CMS
      </Grid2>
      <Grid2 container direction="row">
        <Grid2
          container
          direction="column"
          alignItems="flex-start"
          className={classes.container}
          mr={3}
          pt={3}
          onClick={() => handleClick('contentCollection')}
        >
          <Grid2 pl="5px">
            <UploadIcon className={classes.icon} />
          </Grid2>
          <Grid2 pl={3}>
            <span style={{ fontSize: '24px' }}>Content Collection</span>
          </Grid2>
          <Grid2 pl={3} className={classes.info}>
            <span>Raw files upload</span>
          </Grid2>
        </Grid2>
        <Grid2
          container
          direction="column"
          alignItems="flex-start"
          className={classes.container}
          mr={3}
          pt={3}
          onClick={() => handleClick('contentCreation')}
        >
          <Grid2 pl="5px">
            <AddIcon className={classes.icon} />
          </Grid2>
          <Grid2 pl={3}>
            <span style={{ fontSize: '24px' }}>Content Creation</span>
          </Grid2>
          <Grid2 pl={3} className={classes.info}>
            <span>Add Final Files</span>
          </Grid2>
        </Grid2>
        <Grid2
          container
          direction="column"
          alignItems="flex-start"
          className={classes.container}
          mr={3}
          pt={3}
          onClick={() => handleClick('contentMarketing')}
        >
          <Grid2 pl="5px">
            <EditIcon className={classes.icon} />
          </Grid2>
          <Grid2 pl={3}>
            <span style={{ fontSize: '24px' }}>Content Marketing</span>
          </Grid2>
          <Grid2 pl={3} className={classes.info}>
            <span>Edit details for marketing</span>
          </Grid2>
        </Grid2>
        <Grid2
          container
          direction="column"
          alignItems="flex-start"
          className={classes.container}
          mr={3}
          pt={3}
          onClick={() => handleClick('marketingAnalytics')}
        >
          <Grid2 pl="5px">
            <PublishIcon className={classes.icon} />
          </Grid2>
          <Grid2 pl={3}>
            <span style={{ fontSize: '24px' }}>Marketing Analytics</span>
          </Grid2>
          <Grid2 pl={3} className={classes.info}>
            <span>Publish details for marketing</span>
          </Grid2>
        </Grid2>
      </Grid2>
      <Grid2 container mt={5} direction="column" sx={{ position: 'relative', minHeight: '60vh' }}>
        <Grid2 fontSize="36px" mb={2}>
          Recent Activities
        </Grid2>

        <TableContainer
          className={classes.table}
          sx={{
            flex: 1,
            overflowY: 'auto',
            border: '1px solid #ddd',
            borderRadius: '4px',
          }}
        >
          <Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
            <SortableTableHead
              onRequestSort={() => null}
              order={'asc'}
              orderBy={''}
              headCells={columnHeader}
              headerVariant="subtitle2"
              tableAssetType="activities"
            />

            <TableBody>
              {activities.length > 0 ? (
                activities.map((activity, index) => (
                  <TableRow key={index} >
                    <StyledTableCell className={classes.hover} onClick={() => handleDetailModal(activity)}><span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{activity?.message}</span></StyledTableCell>
                    <StyledTableCell className={classes.hover}>{activity?.fileType}</StyledTableCell>
                    <StyledTableCell className={classes.hover}>
                      {moment(activity.date).format('YYYY-MM-DD')}
                    </StyledTableCell>
                    <StyledTableCell className={classes.hover}>{activity?.userName}</StyledTableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    You don't have any activity yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {openDetailModal && lowerCase(fileType) === 'raw' && documentId && (
            <ContentDetails
              setOpenRawAssetDetailsModal={setOpenDetailModal}
              id={documentId}
              disableEdit
            />
          )}

          {openDetailModal && lowerCase(fileType) === 'pre_final' && documentId && (
            <PreFinalFileContentDetails
              setOpenDetailsModal={setOpenDetailModal}
              id={documentId}
              disableEdit
            />
          )}

          {openDetailModal && lowerCase(fileType) === 'final' && documentId && (
            <FinalFileContentDetails
              setOpenDetailsModal={setOpenDetailModal}
              id={documentId}
              disableEdit
            />
          )}
        </TableContainer>

        {isLoading && (
          <Grid2
            container
            justifyContent="center"
            alignItems="center"
            sx={{
              position: 'absolute',
              top: 60,
              left: 0,
              width: '100%',
              height: 'calc(100% - 60px)',
              background: 'rgba(255,255,255,0.5)',
              zIndex: 10,
            }}
          >
            <CircularProgress />
          </Grid2>
        )}

        <Grid2
          sx={{
            position: 'sticky',
            bottom: 0,
            width: '100%',
            backgroundColor: '#fff',
            borderTop: '1px solid #ddd',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            py: 1,
            px: 2,
          }}
        >
          <TablePagination
            component="div"
            count={totalCount}
            page={listFilterParams.page - 1}
            onPageChange={handleChangePage}
            rowsPerPage={listFilterParams.perPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50, 100]}
            sx={{
              '& .MuiTablePagination-toolbar': {
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                flexWrap: 'nowrap',
              },
              '& .MuiTablePagination-spacer': {
                display: 'none',
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                marginTop: 2,
              },
            }}
          />
        </Grid2>
      </Grid2>
    </Grid2>
  );
};

export default Dashboard;
